import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { MonthField } from "@/components/DateField";
import { fmtMoney } from "@/components/EventCard";
import type { StatementData } from "@/components/Statement";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDate, todayInBaghdad } from "@/lib/time";

const ROWS: [string, string][] = [["opening", ""], ["fees", "+"], ["debit_adj", "+"], ["exemptions", "−"], ["credit_adj", "−"], ["payments", "−"]];
const money = (amount: string, currency: string, sign = "") => <bdi dir="ltr">{sign}{fmtMoney({ amount, currency } as any)}</bdi>;
const monthRange = (ym: string) => {
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
};

const Sheet = ({ d, name, ym }: { d: StatementData; name: string; ym: string }) => {
  const { t, lang } = usePrefs();
  const { from, to } = monthRange(ym);
  const cell = { padding: "6px 8px", borderBottom: "1px solid #e5e5e5" } as React.CSSProperties;
  return (
    <div dir={lang === "ar" ? "rtl" : "ltr"} style={{ width: 760, padding: 32, background: "#fff", color: "#111", fontFamily: "inherit", fontSize: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "3px solid #111", paddingBottom: 12, marginBottom: 16 }}>
        <div><div style={{ fontSize: 22, fontWeight: 900 }}>{t("appName")}</div><div style={{ color: "#666" }}>{t("b8.pdfTitle")}</div></div>
        <div style={{ textAlign: "end" }}><div style={{ fontWeight: 800, fontSize: 15 }}>{name}</div><div style={{ color: "#666" }}>{fmtDate(`${from}T12:00:00Z`, lang)} — {fmtDate(`${to}T12:00:00Z`, lang)}</div></div>
      </div>
      {Object.entries(d.currencies).length === 0 && <p>{t("b7.noEntries")}</p>}
      <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
        {Object.entries(d.currencies).map(([c, v]) => (
          <table key={c} style={{ flex: 1, borderCollapse: "collapse", border: "1px solid #ddd" }}>
            <tbody>
              <tr><td colSpan={2} style={{ ...cell, fontWeight: 900, background: "#f5f5f5" }}>{c}</td></tr>
              {ROWS.map(([k, s]) => <tr key={k}><td style={cell}>{s} {t(`b7.${k}`)}</td><td style={{ ...cell, textAlign: "end" }}>{money(v![k], c)}</td></tr>)}
              <tr><td style={{ ...cell, fontWeight: 900 }}>= {t("b7.closing")}</td><td style={{ ...cell, textAlign: "end", fontWeight: 900 }}>{money(v!.closing, c)}</td></tr>
            </tbody>
          </table>
        ))}
      </div>
      {!!d.entries.length && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#f5f5f5", textAlign: "start" }}>
            {[t("b7.date"), t("b7.kindLabel"), t("b8.details"), t("b7.amount")].map((h) => <th key={h} style={{ ...cell, textAlign: "start" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {[...d.entries].reverse().map((e) => {
              const minus = ["exemption", "credit_adj", "payment"].includes(e.kind);
              const note = e.note && t(`b7.note.${e.note}`) !== `b7.note.${e.note}` ? t(`b7.note.${e.note}`) : e.note;
              return (
                <tr key={e.entry_id}>
                  <td style={cell}>{fmtDate(e.effective_at || e.created_at, lang)}</td>
                  <td style={cell}>{t(`b7.kind.${e.kind}`)}{e.reverses ? ` (${t("b7.reversal")})` : ""}{e.reversed_by ? ` (${t("b7.reversed")})` : ""}</td>
                  <td style={cell}>{[e.event_title_ar ? (lang === "ar" ? e.event_title_ar : e.event_title_en) : "", note, e.reference].filter(Boolean).join(" · ")}</td>
                  <td style={{ ...cell, textAlign: "end", whiteSpace: "nowrap" }}>{money(e.amount, e.currency, minus ? "−" : "+")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p style={{ marginTop: 18, color: "#777", fontSize: 10 }}>{t("b7.formula")} · {t("b8.generated")} {fmtDate(new Date().toISOString(), lang)}</p>
    </div>
  );
};

export const StatementPdfButton = ({ endpoint, name, fileId }: { endpoint: string; name: string; fileId: string }) => {
  const { t } = usePrefs();
  const [ym, setYm] = useState(todayInBaghdad().slice(0, 7));
  const [d, setD] = useState<StatementData | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const run = async () => {
    setBusy(true);
    try {
      const { from, to } = monthRange(ym);
      const { data } = await api.get(`${endpoint}?from=${from}&to=${to}`);
      setD(data);
      await new Promise((r) => setTimeout(r, 120));
      await (document as any).fonts?.ready;
      const html2pdf = (await import("html2pdf.js")).default as any;
      await html2pdf().set({ margin: 8, filename: `statement-${fileId}-${ym}.pdf`, html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }, pagebreak: { mode: ["css", "legacy"] } }).from(ref.current!.firstElementChild).save();
      toast.success(t("b8.pdfReady"));
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
      setD(null);
    }
  };
  return (
    <div className="flex items-end gap-2">
      <label className="text-xs font-bold space-y-1">{t("b8.month")}
        <MonthField value={ym} onChange={setYm} testId="statement-pdf-month" />
      </label>
      <button onClick={run} disabled={busy || !ym} data-testid="statement-pdf-download" className="h-10 px-4 rounded-full border text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
        <FileDown className="h-4 w-4" />{busy ? t("common.loading") : t("b8.downloadPdf")}
      </button>
      {d && createPortal(<div ref={ref} style={{ position: "fixed", left: -10000, top: 0 }}><Sheet d={d} name={name} ym={ym} /></div>, document.body)}
    </div>
  );
};
